import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Descriptions,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Spin,
  Alert,
  Tabs,
  List,
  Avatar,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Upload,
  Modal,
  Drawer,
  Popconfirm,
  Tooltip,
  Cascader,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  BellOutlined,
  PaperClipOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Lead, getLeadById, updateLead } from '../../services/lead';
import { getUsers, User } from '../../services/user';
import { DATA_SOURCE_OPTIONS, URGENCY_LEVEL_OPTIONS } from '../../constants/leadConstants';
import { REGION_DATA } from '../../utils/regionData';
import {
  FollowUp,
  FollowUpTargetType,
  getTargetFollowUps,
  createFollowUp,
  updateFollowUp,
  deleteFollowUp,
  CreateFollowUpRequest,
  UpdateFollowUpRequest,
} from '../../services/followUp';
import { logError } from '../../utils/logger';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const LeadDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [followUpForm] = Form.useForm();

  const [lead, setLead] = useState<Lead | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [followUpModalVisible, setFollowUpModalVisible] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState<FollowUp | null>(null);
  const [relatedCases, setRelatedCases] = useState<any[]>([]);
  const [caseDrawerVisible, setCaseDrawerVisible] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      fetchLeadDetail();
      fetchFollowUps();
      fetchUsers();
      fetchRelatedCases();
    }
  }, [id]);

  const fetchLeadDetail = async () => {
    try {
      setLoading(true);
      const leadData = await getLeadById(Number(id));
      setLead(leadData);
    } catch (error) {
      logError('Failed to fetch lead detail', error);
      message.error('获取线索详情失败');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedCases = async () => {
    console.log('fetchRelatedCases called for lead id:', id);
    try {
      const { getCaseList } = await import('../../services/case');
      console.log('getCaseList imported successfully');
      // 获取所有案件，然后筛选出关联当前线索的案件
      const response = await getCaseList({ page: 1, pageSize: 100 });
      console.log('All cases response:', response);
      const filtered = (response.items || []).filter((c: any) => c.lead_id === Number(id));
      console.log('Related cases for lead', id, ':', filtered);
      setRelatedCases(filtered);
    } catch (error) {
      logError('Failed to fetch related cases', error);
      console.error('Failed to fetch related cases:', error);
    }
  };

  const handleViewCase = (caseId: number) => {
    setSelectedCaseId(caseId);
    setCaseDrawerVisible(true);
  };

  const fetchFollowUps = async () => {
    try {
      setFollowUpsLoading(true);
      const followUpData = await getTargetFollowUps(
        FollowUpTargetType.LEAD,
        Number(id),
        { page: 1, page_size: 50 }
      );
      setFollowUps(followUpData);
    } catch (error) {
      logError('Failed to fetch follow-ups', error);
      message.error('获取跟进记录失败');
    } finally {
      setFollowUpsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await getUsers({ limit: 100 });
      setUsers(usersData.items);
    } catch (error) {
      logError('Failed to fetch users', error);
    }
  };

  const handleEditLead = () => {
    if (lead) {
      form.setFieldsValue({
        contact_name: lead.contact_name,
        phone: lead.phone,
        email: lead.email,
        address: lead.address,
        data_source: lead.data_source,
        lead_type: lead.lead_type,
        case_description: lead.case_description,
        description: lead.description,
        follow_status: lead.follow_status,
        next_follow_time: lead.next_follow_time ? dayjs(lead.next_follow_time) : undefined,
        reminder_enabled: lead.reminder_enabled === 1,
        urgency_level: lead.urgency_level,
        visit_time: lead.visit_time ? dayjs(lead.visit_time) : undefined,
        lead_generated_time: lead.lead_generated_time ? dayjs(lead.lead_generated_time) : undefined,
        assigned_to: lead.assigned_to,
        region: lead.province && lead.city && lead.district ? [lead.province, lead.city, lead.district] : undefined,
      });
      setEditModalVisible(true);
    }
  };

  const handleSaveLead = async () => {
    try {
      const values = await form.validateFields();
      
      // Handle region data
      if (values.region && Array.isArray(values.region)) {
        values.province = values.region[0];
        values.city = values.region[1];
        values.district = values.region[2];
        delete values.region;
      }
      
      const updateData = {
        ...values,
        reminder_enabled: values.reminder_enabled ? 1 : 0,
        next_follow_time: values.next_follow_time?.toISOString(),
        visit_time: values.visit_time?.toISOString(),
        lead_generated_time: values.lead_generated_time?.toISOString(),
      };
      await updateLead(Number(id), updateData);
      message.success('线索信息更新成功');
      setEditModalVisible(false);
      fetchLeadDetail();
    } catch (error) {
      logError('Failed to update lead', error);
      message.error('更新线索信息失败');
    }
  };

  const handleCreateFollowUp = () => {
    followUpForm.resetFields();
    setEditingFollowUp(null);
    setFollowUpModalVisible(true);
  };

  const handleEditFollowUp = (followUp: FollowUp) => {
    followUpForm.setFieldsValue({
      content: followUp.content,
      phone_number: followUp.phone_number,
      responsible_person_id: followUp.responsible_person_id,
      next_follow_up_time: followUp.next_follow_up_time ? dayjs(followUp.next_follow_up_time) : undefined,
      reminder_enabled: followUp.reminder_enabled,
    });
    setEditingFollowUp(followUp);
    setFollowUpModalVisible(true);
  };

  const handleSaveFollowUp = async () => {
    try {
      const values = await followUpForm.validateFields();
      const followUpData = {
        ...values,
        next_follow_up_time: values.next_follow_up_time?.toISOString(),
      };

      if (editingFollowUp) {
        await updateFollowUp(editingFollowUp.id, followUpData);
        message.success('跟进记录更新成功');
      } else {
        const createData: CreateFollowUpRequest = {
          target_type: FollowUpTargetType.LEAD,
          target_id: Number(id),
          follow_type: 'user',  // 明确设置为 user 类型
          ...followUpData,
        };
        await createFollowUp(createData);
        message.success('跟进记录创建成功');
      }

      setFollowUpModalVisible(false);
      fetchFollowUps();
    } catch (error) {
      logError('Failed to save follow-up', error);
      message.error('保存跟进记录失败');
    }
  };

  const handleDeleteFollowUp = async (followUpId: number) => {
    try {
      await deleteFollowUp(followUpId);
      message.success('跟进记录删除成功');
      fetchFollowUps();
    } catch (error) {
      logError('Failed to delete follow-up', error);
      message.error('删除跟进记录失败');
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      public_pool: { color: 'blue', text: '公海池' },
      assigned: { color: 'orange', text: '已分配' },
      converted: { color: 'green', text: '已转化' },
      recycled: { color: 'red', text: '已回收' },
    };
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const followUpItems = (followUps || []).map((followUp) => ({
    title: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Text strong>{followUp.follow_type || '跟进记录'}</Text>
          <Text type="secondary">{dayjs(followUp.created_at).format('YYYY-MM-DD HH:mm')}</Text>
        </Space>
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditFollowUp(followUp)}
          />
          <Popconfirm
            title="确定删除这条跟进记录？"
            onConfirm={() => handleDeleteFollowUp(followUp.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      </div>
    ),
    description: (
      <div>
        <Text>{followUp.content}</Text>
        <div style={{ marginTop: 8 }}>
          <Space wrap>
            {followUp.creator_name && (
              <Tag icon={<UserOutlined />}>创建人: {followUp.creator_name}</Tag>
            )}
            {followUp.responsible_person_name && (
              <Tag icon={<TeamOutlined />}>负责人: {followUp.responsible_person_name}</Tag>
            )}
            {followUp.phone_number && (
              <Tag icon={<PhoneOutlined />}>电话: {followUp.phone_number}</Tag>
            )}
            {followUp.next_follow_up_time && (
              <Tag
                icon={<CalendarOutlined />}
                color={followUp.reminder_enabled ? 'orange' : 'blue'}
              >
                下次跟进: {dayjs(followUp.next_follow_up_time).format('YYYY-MM-DD HH:mm')}
                {followUp.reminder_enabled && <BellOutlined style={{ marginLeft: 4 }} />}
              </Tag>
            )}
            {(followUp.attachments || []).length > 0 && (
              <Tag icon={<PaperClipOutlined />}>
                附件 ({(followUp.attachments || []).length})
              </Tag>
            )}
          </Space>
        </div>
      </div>
    ),
  }));

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!lead) {
    return (
      <Card>
        <Alert
          message="线索不存在"
          description="未找到对应的线索信息"
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div id="lead-detail-container">
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/leads')}
              >
                返回线索列表
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {lead.contact_name} - {lead.lead_number}
              </Title>
              {getStatusTag(lead.status)}
              {relatedCases.length > 0 && (
                <Button
                  type="link"
                  icon={<FileTextOutlined />}
                  onClick={() => handleViewCase(relatedCases[0].id)}
                >
                  关联案件 ({relatedCases.length})
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEditLead}
            >
              编辑线索
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Content */}
      <Tabs defaultActiveKey="info">
        <TabPane tab="基本信息" key="info">
          <Card title="线索信息">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="线索编号">{lead.lead_number}</Descriptions.Item>
              <Descriptions.Item label="状态">{getStatusTag(lead.status)}</Descriptions.Item>
              <Descriptions.Item label="联系人">{lead.contact_name}</Descriptions.Item>
              <Descriptions.Item label="联系电话">
                {lead.phone ? (
                  <Tooltip title={(lead as any).is_blacklisted ? `黑名单：${(lead as any).blacklist_remark || '无备注'}` : ''}>
                    <span style={{ color: (lead as any).is_blacklisted ? '#ff4d4f' : 'inherit' }}>
                      {lead.phone}
                    </span>
                  </Tooltip>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">{lead.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="所在地区">
                {lead.province || lead.city || lead.district ? (
                  `${lead.province || ''}${lead.city || ''}${lead.district || ''}`
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="详细地址">{lead.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="数据来源">{lead.data_source || '-'}</Descriptions.Item>
              <Descriptions.Item label="线索类型">{lead.lead_type || '-'}</Descriptions.Item>
              <Descriptions.Item label="跟进意向">{lead.follow_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="紧急程度">
                <Tag color={
                  lead.urgency_level === 'A类' ? '#ff4d4f' :
                  lead.urgency_level === 'B类' ? '#faad14' :
                  lead.urgency_level === 'C类' ? '#52c41a' : '#1890ff'
                }>
                  {lead.urgency_level || 'D类'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="下次跟进时间">
                {lead.next_follow_time ? (
                  <Space>
                    {dayjs(lead.next_follow_time).format('YYYY-MM-DD HH:mm')}
                    {lead.reminder_enabled === 1 && (
                      <Tooltip title="已启用提醒">
                        <BellOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    )}
                  </Space>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="到访时间">
                {lead.visit_time ? dayjs(lead.visit_time).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="线索产生时间">
                {lead.lead_generated_time ? dayjs(lead.lead_generated_time).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="分配给">{lead.assignee_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="分配时间">
                {lead.assigned_at ? dayjs(lead.assigned_at).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="创建人">{lead.creator_name}</Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(lead.created_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="案件描述" span={2}>
                {lead.case_description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="详细描述" span={2}>
                {lead.description || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>

        <TabPane
          tab={
            <Space>
              <FileTextOutlined />
              跟进记录 ({followUps.length})
            </Space>
          }
          key="followups"
        >
          <Card title="跟进记录">
            {/* Quick Follow-up Input */}
            <div 
              style={{ 
                marginBottom: 16, 
                padding: 16,
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: 8
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <TextArea
                  placeholder="点击输入跟进内容..."
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  maxLength={500}
                  showCount
                  style={{ fontSize: 14 }}
                />
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space>
                      <Tooltip title="附件">
                        <Button icon={<PaperClipOutlined />} size="small" />
                      </Tooltip>
                      <Tooltip title="提醒">
                        <Button icon={<BellOutlined />} size="small" />
                      </Tooltip>
                      <Select
                        placeholder="选择意向"
                        style={{ width: 120 }}
                        size="small"
                        allowClear
                      >
                        <Select.Option value="高">高意向</Select.Option>
                        <Select.Option value="中">中意向</Select.Option>
                        <Select.Option value="低">低意向</Select.Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        size="small"
                        onClick={handleCreateFollowUp}
                      >
                        详细表单
                      </Button>
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        size="small"
                      >
                        发布跟进
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
            </div>

            <Spin spinning={followUpsLoading}>
              {followUps.length > 0 ? (
                <List
                  itemLayout="vertical"
                  dataSource={followUpItems}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<FileTextOutlined />} />}
                        title={item.title}
                        description={item.description}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '50px 0' }}>
                  <Text type="secondary">暂无跟进记录</Text>
                </div>
              )}
            </Spin>
          </Card>
        </TabPane>
      </Tabs>

      {/* Edit Lead Modal */}
      <Modal
        title="编辑线索"
        open={editModalVisible}
        onOk={handleSaveLead}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={800}
      >
        <Form form={form} layout="vertical" autoComplete="off">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contact_name"
                label="联系人"
                rules={[{ required: true, message: '请输入联系人姓名' }]}
              >
                <Input placeholder="请输入联系人姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="联系电话">
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="邮箱">
                <Input placeholder="请输入邮箱地址" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="region" label="所在地区">
                <Cascader
                  options={REGION_DATA}
                  placeholder="请选择省/市/区"
                  // 禁用搜索功能以避免浏览器自动填充
                  // showSearch
                  changeOnSelect
                  popupClassName="region-cascader-popup"
                  getPopupContainer={(trigger) => trigger.parentElement || document.body}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="address" label="详细地址">
                <Input placeholder="请输入详细地址（街道、门牌号等）" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="data_source" label="数据来源">
                <Select placeholder="请选择数据来源" allowClear>
                  {DATA_SOURCE_OPTIONS.map(option => (
                    <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lead_type" label="线索类型">
                <Select placeholder="请选择线索类型" allowClear>
                  <Select.Option value="民事诉讼">民事诉讼</Select.Option>
                  <Select.Option value="刑事辩护">刑事辩护</Select.Option>
                  <Select.Option value="行政诉讼">行政诉讼</Select.Option>
                  <Select.Option value="法律咨询">法律咨询</Select.Option>
                  <Select.Option value="其他">其他</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="follow_status" label="跟进意向">
                <Select placeholder="请选择跟进意向" allowClear>
                  <Select.Option value="未联系">未联系</Select.Option>
                  <Select.Option value="未接通">未接通</Select.Option>
                  <Select.Option value="不需要">不需要</Select.Option>
                  <Select.Option value="无人接听">无人接听</Select.Option>
                  <Select.Option value="无效">无效</Select.Option>
                  <Select.Option value="空号">空号</Select.Option>
                  <Select.Option value="停机">停机</Select.Option>
                  <Select.Option value="稍后联系">稍后联系</Select.Option>
                  <Select.Option value="重复">重复</Select.Option>
                  <Select.Option value="客诉">客诉</Select.Option>
                  <Select.Option value="已加微信">已加微信</Select.Option>
                  <Select.Option value="已加企微">已加企微</Select.Option>
                  <Select.Option value="已付咨询费">已付咨询费</Select.Option>
                  <Select.Option value="邀约到所">邀约到所</Select.Option>
                  <Select.Option value="已成交">已成交</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="urgency_level" label="紧急程度">
                <Select placeholder="请选择紧急程度" allowClear>
                  {URGENCY_LEVEL_OPTIONS.map(option => (
                    <Select.Option key={option.value} value={option.value}>{option.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="next_follow_time" label="下次跟进时间">
                <DatePicker
                  showTime
                  placeholder="选择下次跟进时间"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="reminder_enabled" label="启用提醒" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="visit_time" label="到访时间">
                <DatePicker
                  showTime
                  placeholder="选择到访时间"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lead_generated_time" label="线索产生时间">
                <DatePicker
                  showTime
                  placeholder="选择线索产生时间"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="assigned_to" label="分配给">
            <Select placeholder="请选择负责人" allowClear>
              {(users || []).map(user => (
                <Select.Option key={user.id} value={user.id}>
                  {user.real_name || user.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="case_description" label="案件描述">
            <TextArea rows={3} placeholder="请输入案件描述" />
          </Form.Item>

          <Form.Item name="description" label="详细描述">
            <TextArea rows={4} placeholder="请输入详细描述" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Follow-up Drawer */}
      <Drawer
        title={editingFollowUp ? '编辑跟进记录' : '添加跟进记录'}
        open={followUpModalVisible}
        onClose={() => setFollowUpModalVisible(false)}
        width={600}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setFollowUpModalVisible(false)}>取消</Button>
            <Button type="primary" onClick={handleSaveFollowUp}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={followUpForm} layout="vertical">
          <Form.Item
            name="content"
            label="跟进内容"
            rules={[{ required: true, message: '请输入跟进内容' }]}
          >
            <TextArea rows={4} placeholder="请输入跟进详情" maxLength={800} />
          </Form.Item>

          <Form.Item name="phone_number" label="联系电话">
            <Input placeholder="请输入联系电话" />
          </Form.Item>

          <Form.Item name="responsible_person_id" label="负责人">
            <Select placeholder="请选择负责人" allowClear>
              {(users || []).map(user => (
                <Select.Option key={user.id} value={user.id}>
                  {user.real_name || user.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="next_follow_up_time" label="下次跟进时间">
            <DatePicker
              showTime
              placeholder="选择下次跟进时间"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name="reminder_enabled" label="启用提醒" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 案件详情抽屉 */}
      {selectedCaseId && (
        <Drawer
          title="关联案件详情"
          width={720}
          open={caseDrawerVisible}
          onClose={() => {
            setCaseDrawerVisible(false);
            setSelectedCaseId(null);
          }}
          extra={
            <Button
              type="primary"
              onClick={() => {
                navigate(`/cases/${selectedCaseId}`);
              }}
            >
              查看完整案件
            </Button>
          }
        >
          {relatedCases.length > 0 && (
            <List
              dataSource={relatedCases}
              renderItem={(caseItem: any) => (
                <List.Item
                  key={caseItem.id}
                  style={{
                    cursor: 'pointer',
                    background: caseItem.id === selectedCaseId ? '#f0f5ff' : 'transparent',
                    padding: '12px',
                    borderRadius: '4px',
                  }}
                  onClick={() => setSelectedCaseId(caseItem.id)}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{caseItem.case_name}</Text>
                        <Tag color={caseItem.case_status === 'active' ? 'green' : 'default'}>
                          {caseItem.case_status === 'active' ? '进行中' : caseItem.case_status === 'closed' ? '已结案' : '暂停'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        {caseItem.case_number && <Text type="secondary">案号：{caseItem.case_number}</Text>}
                        {caseItem.case_type && <Text type="secondary">类型：{caseItem.case_type}</Text>}
                        {caseItem.responsible_lawyer_name && (
                          <Text type="secondary">主办律师：{caseItem.responsible_lawyer_name}</Text>
                        )}
                        {caseItem.accept_date && (
                          <Text type="secondary">承接日期：{dayjs(caseItem.accept_date).format('YYYY-MM-DD')}</Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Drawer>
      )}
    </div>
  );
};

export default LeadDetail;